import React, {FC, useEffect, useState} from 'react';
import {AvailableApp, axiosInstance, loginService} from 'services';
import {Apps, AppsUrls, AppUrlsTest, areQuestionsSupported} from 'enums';
import {storage} from "utils";
import {User} from "interfaces";
import {questionsStore, settingsStore} from "../stores";
import {statsStore} from "stores/statsStore";
import {useInterceptors} from "hooks";
import LogRocket from "logrocket";
import {Envs, EnvSetup} from "enums/Envs";
import * as amplitude from "@amplitude/analytics-browser";
import {IdleMonitor} from "components/IdleMonitor";
import {SupportActions} from "enums/SupportActions";

export type SupportActionsMap = Record<SupportActions, boolean>;

const toSupportActionsMap = (enabled: SupportActions[]): SupportActionsMap => {
    const allowed = new Set(enabled);
    return (Object.values(SupportActions) as SupportActions[]).reduce((acc, action) => {
        acc[action] = allowed.has(action);
        return acc;
    }, {} as SupportActionsMap);
};

const emptySupportActionsMap = (): SupportActionsMap => toSupportActionsMap([]);

interface AuthContextType {
    isInitializing: boolean;
    user: User | undefined;
    app: Apps | undefined;
    isTest: boolean | undefined;
    availableApps?: AvailableApp[];
    env: EnvSetup | undefined;
    supportedActions: SupportActionsMap;

    signin: (user: string, app: Apps, isTest: boolean) => Promise<User | undefined>;
    hasRight: (action: SupportActions) => boolean;
    reset: () => void;
    changeApp: (newApp: Apps, isTest: boolean) => Promise<boolean>
}

export const AuthContext = React.createContext<AuthContextType>(null!);

export const AuthProvider: FC<{ children: React.ReactNode }> = ({children}) => {
    const [isInitializing, setIsInitializing] = useState(true)
    const [state, setState] = useState<{
        user: User | undefined;
        app: Apps | undefined,
        isTest: boolean | undefined,
        env: EnvSetup | undefined,
        availableApps?: AvailableApp[],
        supportedActions: SupportActionsMap
    }>({
        user: undefined,
        app: undefined,
        isTest: false,
        env: undefined,
        supportedActions: emptySupportActionsMap()
    });

    const trackUser = (user: { id: string, name: string, isSuperAdmin: boolean }, app: Apps, isTest: boolean) => {
        amplitude.setUserId(user.id);
        const amplitudeIdentify = new amplitude.Identify();
        amplitudeIdentify.set('App', app);
        amplitudeIdentify.add('Session', 1);
        amplitudeIdentify.set('Test', isTest);
        amplitudeIdentify.set('SuperAdmin', user.isSuperAdmin);
        amplitudeIdentify.set('Name', user.name);
        amplitude.identify(amplitudeIdentify, {});
    }


    const loadSession = async (app: Apps, isTest: boolean, availableApps?: AvailableApp[], user?: User) => {
        const env = {env: isTest ? Envs.Test : Envs.Prod, app};
        const u = user ?? await loginService.getUser(app);
        let actionsMap = emptySupportActionsMap()
        if (u) {
            const actions = await loginService.getSupportActions(env);
            actionsMap = toSupportActionsMap(actions);
            setState({
                app,
                user: u,
                isTest,
                env,
                availableApps,
                supportedActions: actionsMap
            });

            if (!isTest) {
                if (app === Apps.Instinct) {
                    LogRocket.init('lovemore/instinct');
                } else if (app === Apps.Flava) {
                    LogRocket.init('lovemore/flava');
                } else if (app === Apps.Bloom) {
                    LogRocket.init('lovemore/bloom-0f2rz');
                }

                LogRocket.identify(u.id, {
                    name: u.name,
                    isSuperAdmin: u.isSuperAdmin
                });
            }

            trackUser(u, app, isTest);

            if (actionsMap[SupportActions.GetAdmins]) {
                statsStore.loadAdmins(app)
            }
        } else {
            amplitude.reset();
        }

        if (areQuestionsSupported(app)) {
            await questionsStore.loadQuestions();
        }
        if (u) {
            settingsStore.reset(env, actionsMap)
        }

        return u;
    }

    const signin = async (token: string, newApp: Apps, isTest: boolean) => {
        axiosInstance.defaults.baseURL = isTest ? AppUrlsTest[newApp] : AppsUrls[newApp];
        const response = await loginService.login(token, newApp);

        if (response.token && response.refresh && response.user) {
            storage.setToken(response.token);
            storage.setRefresh(response.refresh);
            storage.setUserId(response.user.id);
            storage.setApp(newApp)
            storage.setIsTest(isTest)
            response.availableApps && storage.setAvailableApps(response.availableApps)
            response.supportUserKey && storage.setSupportUserKey(response.supportUserKey)

            return loadSession(newApp, isTest, response.availableApps, response.user);
        }
    };

    const changeApp = async (newApp: Apps, isTest: boolean) => {
        const supportUserKey = storage.getSupportUserKey()
        const refreshToken = storage.getRefresh()
        if (!state.user || !supportUserKey || !refreshToken) {
            return false
        }
        axiosInstance.defaults.baseURL = isTest ? AppUrlsTest[newApp] : AppsUrls[newApp];
        const response = await loginService.refresh(refreshToken, state.user.id, newApp, supportUserKey);
        if (response && response.token && response.refresh && response.user) {
            storage.setToken(response.token);
            storage.setRefresh(response.refresh);
            storage.setUserId(typeof response.user === "string" ? response.user : response.user.id);
            storage.setApp(newApp)
            storage.setIsTest(isTest)
            await loadSession(newApp, isTest, state.availableApps);
            return true;
        } else {
            storage.setSupportUserKey()
        }

        return false
    }

    const reset = () => {
        amplitude.reset();
        setState({
            app: undefined,
            user: undefined,
            isTest: undefined,
            env: undefined,
            supportedActions: emptySupportActionsMap()
        })
    }

    const hasRight = (action: SupportActions) => {
        return state.supportedActions[action]
    }

    const {isInterceptorsConfigured} = useInterceptors(reset, state.app);

    useEffect(() => {
        const app = storage.getApp();
        const isTest = storage.getIsTest();
        setState({
            app,
            user: undefined,
            isTest,
            env: {env: isTest ? Envs.Test : Envs.Prod, app},
            supportedActions: emptySupportActionsMap()
        });
    }, []);


    useEffect(() => {
        if (!isInterceptorsConfigured) {
            return
        }
        const app = storage.getApp();
        if (app) {
            axiosInstance.defaults.baseURL = storage.getIsTest() ? AppUrlsTest[app] : AppsUrls[app];
            loadSession(app, storage.getIsTest(), storage.getAvailableApps())
                .finally(() => setIsInitializing(false))
        } else {
            setIsInitializing(false);
        }
    }, [isInterceptorsConfigured])


    let value = {
        ...state,
        isInitializing: isInitializing || !isInterceptorsConfigured,
        hasRight,
        signin,
        reset,
        changeApp
    };

    return <AuthContext.Provider value={value}>
        <IdleMonitor isSuperAdmin={state.user?.isSuperAdmin}
                     userId={state.user?.id} onIdle={reset}/>
        {children}
    </AuthContext.Provider>;
};
